import { useAuth } from '@/contexts/AuthContext';
import { useDownloadFetchProgress } from '@/hooks/download/useDownloadFetchProgress';
import { useProgressToast } from '@/hooks/progress/useProgressToast';
import { useJobPolling } from '@/hooks/useJobPolling';
import { BATCH_IMAGES_CHUNK_SIZE } from '@/lib/constants/batches';
import {
  DOWNLOAD_IMAGES_JOB_KEY,
  DOWNLOAD_IMAGES_PROGRESS_KEY,
  RESUME_DOWNLOAD_JOB_KEY,
} from '@/lib/constants/job';
import { clearPersistedDownloadJob, getPersistedDownloadJob } from '@/lib/download/download';
import { promptSaveFileHandle } from '@/lib/download/save-file-handle';
import { getSessionActiveJobId, setSessionActiveJobId } from '@/lib/download/download-job-session';
import { DOWNLOAD_CANCELLED_MESSAGE } from '@/lib/download/zip-download-core';
import { JobService } from '@/services/job.service';
import { DownloadProjectImagesResult } from '@/types/download';
import { JobStatus, JobType } from '@/types/schema';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

export function useResumeDownload() {
  const { user, loading, isInitialized } = useAuth();
  const [resumeJobId, setResumeJobId] = useState<string | null>(null);
  const [progressDescription, setProgressDescription] = useState('Preparando la descarga...');
  const attemptedRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const finishProgressRef = useRef<((onDone?: () => void) => void) | null>(null);
  const cancelProgressRef = useRef<(() => void) | null>(null);
  const cancelZipDownloadRef = useRef<() => void>(() => undefined);
  const isCancelledRef = useRef(false);
  const isActiveRef = useRef(false);
  const operationTokenRef = useRef(0);

  const resetResumeState = useCallback((clearPersistedJob = true) => {
    isActiveRef.current = false;
    if (clearPersistedJob) {
      clearPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
    }
    setSessionActiveJobId(null);
    setResumeJobId(null);
    toast.dismiss(RESUME_DOWNLOAD_JOB_KEY);
  }, []);

  const handleCancelResumeDownload = useCallback(() => {
    if (!isActiveRef.current || isCancelledRef.current) return;
    isCancelledRef.current = true;
    operationTokenRef.current += 1;
    cancelProgressRef.current?.();
    cancelZipDownloadRef.current();
    resetResumeState();
    toast.success('Descarga cancelada', { id: DOWNLOAD_IMAGES_PROGRESS_KEY });
  }, [resetResumeState]);

  const { downloadWithProgress, cancelDownload } = useDownloadFetchProgress({
    onCancel: handleCancelResumeDownload,
  });

  const isExpectedCancellation = useCallback(
    (error: unknown) =>
      isCancelledRef.current ||
      (error instanceof Error && error.message === DOWNLOAD_CANCELLED_MESSAGE),
    [],
  );

  const {
    status,
    progress: jobProgress,
    currentBatch,
    totalImages,
    totalBatches,
  } = useJobPolling({
    jobId: resumeJobId,
    onCompleted: (result) => {
      if (isCancelledRef.current) return;
      const downloadToken = operationTokenRef.current;
      const signedUrls = (result as DownloadProjectImagesResult).signedUrls;
      const zipName = getPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY)?.zipName ?? 'imagenes';
      toast.dismiss(RESUME_DOWNLOAD_JOB_KEY);
      setResumeJobId(null);
      finishProgressRef.current?.(() => {
        void (async () => {
          try {
            if (isCancelledRef.current || operationTokenRef.current !== downloadToken) return;
            const { failedCount } = await downloadWithProgress(signedUrls, zipName);
            if (isCancelledRef.current || operationTokenRef.current !== downloadToken) return;
            toast.success('Imágenes descargadas correctamente', {
              id: DOWNLOAD_IMAGES_PROGRESS_KEY,
            });
            if (failedCount > 0) {
              toast(
                `${failedCount} ${failedCount === 1 ? 'imagen no se pudo descargar' : 'imágenes no se pudieron descargar'}`,
                {
                  id: `${DOWNLOAD_IMAGES_PROGRESS_KEY}-partial-failure`,
                  icon: '⚠️',
                },
              );
            }
          } catch (error) {
            if (isExpectedCancellation(error)) return;
            toast.error('Error al descargar las imágenes. Intenta de nuevo.', {
              id: DOWNLOAD_IMAGES_PROGRESS_KEY,
            });
          } finally {
            if (operationTokenRef.current === downloadToken) {
              resetResumeState();
            }
          }
        })();
      });
    },
    onFailed: () => {
      if (isCancelledRef.current) return;
      cancelProgressRef.current?.();
      toast.error('Error al preparar la descarga. Intenta de nuevo.', {
        id: DOWNLOAD_IMAGES_PROGRESS_KEY,
      });
      resetResumeState();
    },
    onTimeout: () => {
      if (isCancelledRef.current) return;
      cancelProgressRef.current?.();
      toast.error('La descarga tardó demasiado. Intenta de nuevo.', {
        id: DOWNLOAD_IMAGES_PROGRESS_KEY,
      });
      resetResumeState();
    },
    onAborted: () => {
      if (isCancelledRef.current) return;
      cancelProgressRef.current?.();
      resetResumeState(false);
    },
  });

  const {
    startControlled,
    finish: finishProgress,
    cancel: cancelProgress,
  } = useProgressToast({
    label: 'Procesando descarga...',
    description: progressDescription,
    toastId: DOWNLOAD_IMAGES_PROGRESS_KEY,
    onCancel: handleCancelResumeDownload,
    cancelLabel: 'Cancelar descarga',
    batchDrive: {
      enabled: resumeJobId != null && status === JobStatus.RUNNING,
      jobProgress,
      currentBatch,
      totalLength: totalImages,
      totalBatches,
    },
  });

  useEffect(() => {
    finishProgressRef.current = finishProgress;
    cancelProgressRef.current = cancelProgress;
  }, [finishProgress, cancelProgress]);

  useEffect(() => {
    cancelZipDownloadRef.current = cancelDownload;
  }, [cancelDownload]);

  useEffect(() => {
    if (!resumeJobId || !status) return;
    if (jobProgress == null) {
      setProgressDescription('Esperando en cola...');
    } else if (status === JobStatus.RUNNING) {
      if (currentBatch != null && totalImages != null && totalImages > 0) {
        const processedSoFar = Math.min(
          totalImages,
          Math.max(0, currentBatch * BATCH_IMAGES_CHUNK_SIZE),
        );
        setProgressDescription(`${processedSoFar} de ${totalImages} imágenes procesadas...`);
      } else {
        setProgressDescription('Preparando archivos para descargar...');
      }
    } else if (status === JobStatus.COMPLETED) {
      setProgressDescription('Archivos preparados para descargar');
    }
  }, [resumeJobId, status, currentBatch, totalImages, jobProgress]);

  useEffect(() => {
    if (!isInitialized || loading || !user) return;
    if (attemptedRef.current) return;

    const persisted = getPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
    if (!persisted) return;
    const activeId = getSessionActiveJobId();
    if (activeId === persisted.jobId) return;

    attemptedRef.current = true;
    isCancelledRef.current = false;
    isActiveRef.current = true;
    operationTokenRef.current += 1;

    void (async () => {
      const persistedAtStart = persisted;
      const resumeToken = operationTokenRef.current;
      try {
        const job = await JobService.getJob(persistedAtStart.jobId);
        if (isCancelledRef.current || operationTokenRef.current !== resumeToken) return;
        const current = getPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
        if (!current || current.jobId !== persistedAtStart.jobId) {
          isActiveRef.current = false;
          return;
        }
        if (job.status === JobStatus.COMPLETED) {
          const signedUrls =
            job.type === JobType.IMAGE_DOWNLOAD
              ? (job.result as DownloadProjectImagesResult | undefined)?.signedUrls
              : undefined;
          if (!signedUrls?.length) {
            clearPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
            isActiveRef.current = false;
            return;
          }
          toast.custom(
            (t) => (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-md text-sm text-gray-800 max-w-md">
                <p className="mb-2">Tu descarga anterior está lista.</p>
                <button
                  type="button"
                  className="text-blue-600 font-medium hover:underline"
                  onClick={() => {
                    void (async () => {
                      toast.dismiss(t.id);
                      try {
                        const zipName = persistedAtStart.zipName ?? 'imagenes';
                        const saveHandle = await promptSaveFileHandle(zipName);
                        if (isCancelledRef.current || operationTokenRef.current !== resumeToken) {
                          return;
                        }
                        const { failedCount } = await downloadWithProgress(
                          signedUrls,
                          zipName,
                          saveHandle,
                        );
                        if (isCancelledRef.current || operationTokenRef.current !== resumeToken) {
                          return;
                        }
                        toast.success('Imágenes descargadas correctamente', {
                          id: DOWNLOAD_IMAGES_PROGRESS_KEY,
                        });
                        if (failedCount > 0) {
                          toast(
                            `${failedCount} ${failedCount === 1 ? 'imagen no se pudo descargar' : 'imágenes no se pudieron descargar'}`,
                            {
                              id: `${DOWNLOAD_IMAGES_PROGRESS_KEY}-partial-failure`,
                              icon: '⚠️',
                            },
                          );
                        }
                      } catch (error) {
                        if (isExpectedCancellation(error)) return;
                        toast.error('Error al descargar las imágenes. Intenta de nuevo.', {
                          id: DOWNLOAD_IMAGES_PROGRESS_KEY,
                        });
                      } finally {
                        if (operationTokenRef.current === resumeToken) {
                          resetResumeState();
                        }
                      }
                    })();
                  }}
                >
                  Descargar imágenes
                </button>
                <button
                  type="button"
                  className="ml-3 text-gray-500 font-medium hover:underline"
                  onClick={handleCancelResumeDownload}
                >
                  Cancelar descarga
                </button>
              </div>
            ),
            { id: RESUME_DOWNLOAD_JOB_KEY, duration: Infinity },
          );
          return;
        }

        if (job.status === JobStatus.FAILED || job.status === JobStatus.CANCELLED) {
          toast.custom(
            (t) => (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-md text-sm text-gray-800 max-w-md">
                <p className="mb-2">Error al preparar la descarga. Intenta de nuevo.</p>
                <button
                  type="button"
                  className="text-blue-600 font-medium hover:underline"
                  onClick={() => {
                    toast.dismiss(t.id);
                  }}
                >
                  Cerrar
                </button>
              </div>
            ),
            { id: RESUME_DOWNLOAD_JOB_KEY, duration: 8000 },
          );
          clearPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
          isActiveRef.current = false;
          return;
        }

        setSessionActiveJobId(persistedAtStart.jobId);
        setProgressDescription('Preparando la descarga...');
        startControlled();
        setResumeJobId(persistedAtStart.jobId);
      } catch (e) {
        if (e instanceof Error && e.message === 'Not Found') {
          clearPersistedDownloadJob(DOWNLOAD_IMAGES_JOB_KEY);
          isActiveRef.current = false;
          return;
        }
        toast.error('No se pudo recuperar el estado de la descarga.');
        isActiveRef.current = false;
      }
    })();
  }, [
    user,
    loading,
    isInitialized,
    downloadWithProgress,
    startControlled,
    handleCancelResumeDownload,
    isExpectedCancellation,
    resetResumeState,
  ]);
}
